import type { DiagramComponentInstance } from '../types/DiagramComponentInstance';
import type { PowerFlowDirection } from '../validators/fields/PowerFlowDirection';
import type { ProtectionDirection } from '../validators/fields/ProtectionDirection';
import { isProtectionDevice } from './DiagramHelpers';

export type FlowDirection = 'unidirectional' | 'bidirectional';

export type PowerFlowSense = 'portZeroToPortOne' | 'portOneToPortZero';

export type CurrentOSDesignation = 'bPbF' | 'bPuF' | 'uPbF' | 'uPuF';

type DirectionalitySpecifications = {
    electrical?: {
        protectionDirection?: ProtectionDirection | null;
        ports?: ({ powerFlowDirection?: PowerFlowDirection | null } | undefined)[];
    };
};

const electricalSpecifications = (componentInstance: DiagramComponentInstance) =>
    (componentInstance.specifications as DirectionalitySpecifications).electrical;

export const getProtectionDirection = (componentInstance: DiagramComponentInstance): ProtectionDirection | null => {
    if (!isProtectionDevice(componentInstance.componentType)) {
        return null;
    }

    return electricalSpecifications(componentInstance)?.protectionDirection ?? null;
};

export const getPortPowerFlowDirection = (
    componentInstance: DiagramComponentInstance,
    port: number,
): PowerFlowDirection | null => {
    const configured = componentInstance.configuration?.ports?.[port]?.powerFlowDirection;

    if (configured && !isProtectionDevice(componentInstance.componentType)) {
        return configured;
    }

    return electricalSpecifications(componentInstance)?.ports?.[port]?.powerFlowDirection ?? null;
};

export const getFlowDirection = (componentInstance: DiagramComponentInstance): FlowDirection | null => {
    const portCount = Math.max(
        componentInstance.configuration?.ports?.length ?? 0,
        electricalSpecifications(componentInstance)?.ports?.length ?? 0,
    );

    const directions = Array.from({ length: portCount }, (_, port) =>
        getPortPowerFlowDirection(componentInstance, port),
    ).filter((direction): direction is PowerFlowDirection => direction !== null);

    if (directions.length === 0) {
        return null;
    }

    return directions.some((direction) => direction === 'bidirectional') ? 'bidirectional' : 'unidirectional';
};

export const getPowerFlowSense = (componentInstance: DiagramComponentInstance): PowerFlowSense | null => {
    if (getFlowDirection(componentInstance) !== 'unidirectional') {
        return null;
    }

    const portZero = getPortPowerFlowDirection(componentInstance, 0);

    if (portZero) {
        return portZero === 'input' ? 'portZeroToPortOne' : 'portOneToPortZero';
    }

    const portOne = getPortPowerFlowDirection(componentInstance, 1);

    if (portOne) {
        return portOne === 'input' ? 'portOneToPortZero' : 'portZeroToPortOne';
    }

    return null;
};

export const getCurrentOSDesignation = (componentInstance: DiagramComponentInstance): CurrentOSDesignation | null => {
    const protection = getProtectionDirection(componentInstance);
    const flow = getFlowDirection(componentInstance);

    if (!protection || !flow) {
        return null;
    }

    const protectionSymbol = protection === 'bidirectional' ? 'bP' : 'uP';
    const flowSymbol = flow === 'bidirectional' ? 'bF' : 'uF';

    return `${protectionSymbol}${flowSymbol}` as const;
};

export const hasProtectionDirectionArrow = (componentInstance: DiagramComponentInstance): boolean =>
    getProtectionDirection(componentInstance) === 'unidirectional';

export const hasBidirectionalProtectionOnUnidirectionalFlow = (
    componentInstance: DiagramComponentInstance,
): boolean => {
    return getCurrentOSDesignation(componentInstance) === 'bPuF';
};
